// src/utils/errorReporter.ts
//
// Reports uncaught browser errors to the central SI error dashboard at
// `${VITE_SI_BACKEND_URL}/errors/report`. Note: for THIS app, the SI
// backend is its own backend — the loop is closed but harmless. If the
// API is down, the reporter just fails silently. Inert when
// VITE_ERROR_INGEST_TOKEN is empty.

const APP_ID = 'si-frontend';

const TOKEN: string =
  (import.meta.env.VITE_ERROR_INGEST_TOKEN as string | undefined) ?? '';

const ENDPOINT: string = (() => {
  const base =
    ((import.meta.env.VITE_SI_BACKEND_URL as string | undefined) ||
      (import.meta.env.PROD
        ? 'https://api.snowbirdindustries.us/api'
        : 'http://localhost:3001/api')).replace(/\/$/, '');
  return `${base}/errors/report`;
})();

const isConfigured = (): boolean => TOKEN.length > 0;

const truncate = (value: unknown, max: number): string | null => {
  if (value === null || value === undefined) return null;
  const s = typeof value === 'string' ? value : String(value);
  return s.length > max ? s.slice(0, max) : s;
};

export interface ErrorReportContext {
  severity?: 'fatal' | 'error' | 'warning' | 'info';
  source?: string;
  httpStatus?: number;
  httpUrl?: string;
  httpMethod?: string;
  httpResponse?: string;
  context?: Record<string, unknown>;
}

export const reportError = (error: unknown, ctx: ErrorReportContext = {}): void => {
  if (!isConfigured()) return;
  try {
    const err =
      error instanceof Error ? error : new Error(typeof error === 'string' ? error : JSON.stringify(error));

    const payload: Record<string, unknown> = {
      appId: APP_ID,
      appVersion: (import.meta.env.VITE_APP_VERSION as string | undefined) || null,
      platform: 'web',
      severity: ctx.severity || 'error',
      message: truncate(`${err.name}: ${err.message || '(no message)'}`, 2000),
      stackTrace: truncate(err.stack, 16_000),
      source: ctx.source || window.location?.pathname || null,
      httpStatus: ctx.httpStatus ?? null,
      httpUrl: ctx.httpUrl ?? null,
      httpMethod: ctx.httpMethod ?? null,
      httpResponse: ctx.httpResponse ? truncate(ctx.httpResponse, 4000) : null,
      context: ctx.context ?? null,
      deviceInfo: {
        runtime: 'browser',
        userAgent: navigator.userAgent,
        language: navigator.language,
        screen: `${window.screen?.width ?? '?'}x${window.screen?.height ?? '?'}`,
      },
    };

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 5000);

    fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Error-Token': TOKEN,
      },
      body: JSON.stringify(payload),
      keepalive: true,
      signal: controller.signal,
    })
      .catch((e) => {
        try { console.warn('[errorReporter] POST failed:', (e as Error)?.message ?? e); } catch { /* noop */ }
      })
      .finally(() => clearTimeout(timer));
  } catch (e) {
    try { console.warn('[errorReporter] internal failure:', (e as Error)?.message ?? e); } catch { /* noop */ }
  }
};

let installed = false;

export const installClientErrorHandlers = (): void => {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event: ErrorEvent) => {
    const err = event.error || new Error(event.message || 'window.onerror');
    reportError(err, {
      severity: 'error',
      source: `${event.filename || 'window.onerror'}:${event.lineno ?? '?'}:${event.colno ?? '?'}`,
    });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event.reason ?? 'unhandledrejection';
    reportError(reason, { severity: 'error', source: 'window.unhandledrejection' });
  });
};
